import axios, { AxiosInstance } from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

const axiosInstance: AxiosInstance = axios.create({
  baseURL: `${API_BASE_URL}/api`,
  // KRITIK: Session cookie gonderilsin — aksi halde backend Session.GetInt32("UserId")
  // her istekte NULL doner, admin check basarisiz. (2026-08-08 Rol: hatasi)
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 2026-08-13 (kullanici direktifi): "Testerlar hatanin gercek nedenini goremiyor,
// bize 'json hatasi' diye rapor ediyorlar. Bir hafta WebDAV yuzunden ugrastik."
//
// Bu interceptor:
//   1) Sunucu JSON yerine HTML dondurdugunde (IIS hata sayfasi) bunu TESPIT eder
//   2) Olasi kok nedeni TURKCE aciklar (WebDAV, oturum, rota vb.)
//   3) Hatayi backend'e POST eder -> ErrorLogs tablosuna dusar (/#/system-errors)
//   4) Kullaniciya referans no gosterir
const diagnose = (status: number | undefined, method: string, body: any): string => {
  const isHtml = typeof body === 'string' && /<!DOCTYPE|<html/i.test(body);
  const writeVerb = ['put', 'delete', 'patch'].includes((method || '').toLowerCase());

  if (status === 405 && writeVerb)
    return 'SUNUCU AYARI: IIS WebDAV modulu PUT/DELETE isteklerini engelliyor. ' +
           'Cozum: web.config icinde WebDAVModule ve WebDAV handler kaldirilmali ' +
           '(veya IIS > Roller > WebDAV Publishing kaldirilmali).';
  if (status === 405) return 'Bu adres icin HTTP metodu tanimli degil (rota uyusmazligi).';
  if (status === 404 && isHtml) return 'Adres bulunamadi ve sunucu HTML dondu — API rotasi yanlis olabilir.';
  if (status === 404) return 'Adres bulunamadi (frontend/backend rota adi uyusmuyor).';
  if (status === 401) return 'Oturum suresi doldu. Lutfen tekrar giris yapin.';
  if (status === 403) return 'Bu islem icin yetkiniz yok.';
  if (status === 415) return 'Icerik tipi hatali (application/json bekleniyor).';
  if (status === 500 && isHtml)
    return 'Sunucu hatasi ve HTML yanit dondu — IIS ayrintili hata sayfasi aktif olabilir.';
  if (status === 503) return 'Sunucu gecici olarak kapali (uygulama yeniden baslatiliyor olabilir).';
  if (!status) return 'Sunucuya ulasilamadi. Ag baglantisi veya IIS servisi kontrol edilmeli.';
  if (isHtml) return `Sunucu JSON yerine HTML dondu (HTTP ${status}). Gercek hata IIS tarafinda.`;
  return '';
};

// Sonsuz dongu korumasi — client-error endpoint'inin kendi hatasini raporlama
let reportingInFlight = false;

axiosInstance.interceptors.response.use(
  (response) => response.data,
  async (error) => {
    const status: number | undefined = error.response?.status;
    const method: string = error.config?.method || '';
    const url: string = (error.config?.baseURL || '') + (error.config?.url || '');
    const body = error.response?.data;
    const hint = diagnose(status, method, body);

    // Konsola AYRINTILI yaz — tester ekran goruntusu alirsa bile anlasilsin
    console.error(
      `%c[API HATASI] ${method.toUpperCase()} ${url} -> HTTP ${status ?? 'BAGLANTI YOK'}`,
      'color:#fff;background:#c62828;padding:2px 6px;border-radius:3px;font-weight:bold'
    );
    if (hint) console.error('%cOLASI SEBEP: ' + hint, 'color:#e65100;font-weight:bold');
    console.error('Sunucu yaniti:', body);

    // Backend'e raporla (ErrorLogs)
    if (!reportingInFlight && !url.includes('/system/client-error')) {
      reportingInFlight = true;
      try {
        const bodyText = typeof body === 'string'
          ? body.substring(0, 4000)
          : JSON.stringify(body ?? {}).substring(0, 4000);
        await axios.post(`${API_BASE_URL}/api/system/client-error`, {
          message: error.message || `HTTP ${status}`,
          url, method: method.toUpperCase(), statusCode: status ?? 0,
          responseBody: bodyText, hint,
        }, { withCredentials: true, timeout: 5000 });
      } catch { /* raporlama basarisiz olursa sessiz gec */ }
      finally { reportingInFlight = false; }
    }

    // 2026-08-13: Oturum gercekten dustuyse (cerez de gecersiz) kullaniciyi bozuk
    // arayuzde birakma — eksik menulu ekran yerine temiz sekilde giris sayfasina gonder.
    // Backend SessionRestoreMiddleware cerezden oturumu geri yukleyemediyse buraya duser.
    if (status === 401 && !url.includes('/auth/login')) {
      try {
        localStorage.removeItem('authUser');
        localStorage.removeItem('token');
        sessionStorage.clear();
      } catch { /* yoksay */ }
      if (!window.location.hash.startsWith('#/login')) {
        window.location.hash = '#/login';
        window.location.reload();
      }
    }

    // Cagiran kod okuyabilsin diye ipucunu error nesnesine ekle
    (error as any).userHint = hint;
    (error as any).referenceNo = (body && typeof body === 'object' ? (body as any).referenceNo : null);
    throw error;
  }
);

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

// Helper methods with axios instance access
const api = {
  get: <T>(url: string) => axiosInstance.get<T, ApiResponse<T>>(url),
  post: <T>(url: string, data?: unknown) => axiosInstance.post<T, ApiResponse<T>>(url, data),
  put: <T>(url: string, data?: unknown) => axiosInstance.put<T, ApiResponse<T>>(url, data),
  patch: <T>(url: string, data?: unknown) => axiosInstance.patch<T, ApiResponse<T>>(url, data),
  delete: <T>(url: string) => axiosInstance.delete<T, ApiResponse<T>>(url),
  uploadFile: <T>(url: string, formData: FormData) =>
    axiosInstance.post<T, ApiResponse<T>>(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }),
  // Axios instance'a doğrudan erişim (token yönetimi için)
  defaults: axiosInstance.defaults,
  setAuthToken: (token: string | null) => {
    if (token) {
      axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axiosInstance.defaults.headers.common['Authorization'];
    }
  },
};

export default api;
